export abstract class WebWorker {
  protected type = "message";
  private worker: Worker;
  protected _workerType: string;
  protected _workerIndex: number;
  // Set once this worker's underlying native Worker has fired "error" or
  // "messageerror". A crashed worker keeps failing the same way for any
  // future job — WebWorkerQueue checks this before reusing the instance at
  // its pool slot, so a dead worker gets terminated and replaced instead of
  // being handed the next job and failing again silently.
  private _crashed = false;

  constructor(worker: Worker) {
    this.worker = worker;
    // A native Worker script that fails to load (bad chunk path, a stale
    // reference surviving a dev-server HMR reload, an uncaught synchronous
    // error in the worker's top-level scope) never posts a "message" back —
    // it fires the browser's own "error"/"messageerror" events instead, which
    // nothing here listened for previously. Without this, that failure mode
    // is silent forever: no resolve, no reject, no callback — exactly the
    // "scenario"/"revaluation" processes that never end. onWorkerError is a
    // no-op here; subclasses override it to reject/callback with an error.
    this.worker?.addEventListener("error", (event: ErrorEvent) => {
      this._crashed = true;
      this.onWorkerError(event);
    });
    this.worker?.addEventListener("messageerror", (event: MessageEvent) => {
      this._crashed = true;
      this.onWorkerError(event);
    });
  }

  get crashed(): boolean {
    return this._crashed;
  }

  protected onWorkerError(_event: ErrorEvent | MessageEvent): void {
    // Implemented in subclass if required
  }

  protected addEventListener(type: string, listener: (evt: MessageEvent) => void): void {
    this.worker?.addEventListener(type, listener as any);
  }

  protected removeEventListener(type: string, listener: (evt: MessageEvent) => void): void {
    this.worker?.removeEventListener(type, listener as any);
  }

  onMessage<T>(observer: (value: T) => void): void {
    this.addEventListener(this.type, (message) => observer(message.data));
  }

  postMessage<T>(message: T, transfers?: Transferable[]): boolean {
    this.worker?.postMessage(message, transfers);
    return !!this.worker;
  }

  terminate(): void {
    this.worker?.terminate();
    this.worker = null;
  }

  get workerType(): string {
    return this._workerType;
  }

  set workerIndex(workerIndex: number) {
    this._workerIndex = workerIndex;
  }

  get workerIndex(): number {
    return this._workerIndex;
  }
}
